// =============================================================================
// Drag & Resize Hook for Template Builder
// =============================================================================
// Manages pointer event state for drag-to-move and corner-resize interactions.
// Uses Pointer Events with setPointerCapture for reliable tracking.
// =============================================================================

import { useState, useCallback, useRef, useEffect } from 'react';
import type { FieldPosition, ResizeHandle } from '../pages/admin/esign/components/FieldOverlay';

// =============================================================================
// Utilities
// =============================================================================

function clamp(val: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, val));
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

// =============================================================================
// Types
// =============================================================================

interface InteractionState {
  type: 'dragging' | 'resizing';
  fieldId: string;
  handle?: ResizeHandle;
  pointerId: number;
  startPointerX: number;
  startPointerY: number;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
}

interface UseDragResizeOptions {
  containerRef: React.RefObject<HTMLDivElement>;
  onPositionChange: (fieldId: string, pos: FieldPosition) => void;
  onPositionCommit: (fieldId: string, pos: FieldPosition) => void;
}

interface UseDragResizeReturn {
  interactionState: InteractionState | null;
  startDrag: (e: React.PointerEvent, fieldId: string, currentPos: FieldPosition) => void;
  startResize: (e: React.PointerEvent, fieldId: string, currentPos: FieldPosition, handle: ResizeHandle) => void;
}

const MIN_SIZE_PERCENT = 2;

// =============================================================================
// Hook
// =============================================================================

export function useDragResize({
  containerRef,
  onPositionChange,
  onPositionCommit,
}: UseDragResizeOptions): UseDragResizeReturn {
  const [interaction, setInteraction] = useState<InteractionState | null>(null);
  const interactionRef = useRef<InteractionState | null>(null);
  const lastPositionRef = useRef<FieldPosition | null>(null);

  // Keep ref in sync for use in event handlers
  interactionRef.current = interaction;

  const startDrag = useCallback(
    (e: React.PointerEvent, fieldId: string, currentPos: FieldPosition) => {
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      const state: InteractionState = {
        type: 'dragging',
        fieldId,
        pointerId: e.pointerId,
        startPointerX: e.clientX,
        startPointerY: e.clientY,
        startX: currentPos.xPercent,
        startY: currentPos.yPercent,
        startW: currentPos.widthPercent,
        startH: currentPos.heightPercent,
      };

      setInteraction(state);
      interactionRef.current = state;
      lastPositionRef.current = null;
    },
    []
  );

  const startResize = useCallback(
    (e: React.PointerEvent, fieldId: string, currentPos: FieldPosition, handle: ResizeHandle) => {
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      const state: InteractionState = {
        type: 'resizing',
        fieldId,
        handle,
        pointerId: e.pointerId,
        startPointerX: e.clientX,
        startPointerY: e.clientY,
        startX: currentPos.xPercent,
        startY: currentPos.yPercent,
        startW: currentPos.widthPercent,
        startH: currentPos.heightPercent,
      };

      setInteraction(state);
      interactionRef.current = state;
      lastPositionRef.current = null;
    },
    []
  );

  // Global pointer move handler
  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      const state = interactionRef.current;
      if (!state || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const containerW = rect.width;
      const containerH = rect.height;

      const dx = e.clientX - state.startPointerX;
      const dy = e.clientY - state.startPointerY;
      const dxPercent = (dx / containerW) * 100;
      const dyPercent = (dy / containerH) * 100;

      let newPos: FieldPosition;

      if (state.type === 'dragging') {
        const newX = clamp(state.startX + dxPercent, 0, 100 - state.startW);
        const newY = clamp(state.startY + dyPercent, 0, 100 - state.startH);
        newPos = {
          xPercent: round4(newX),
          yPercent: round4(newY),
          widthPercent: state.startW,
          heightPercent: state.startH,
        };
      } else {
        // Resizing
        const handle = state.handle!;
        let newX = state.startX;
        let newY = state.startY;
        let newW = state.startW;
        let newH = state.startH;

        // Horizontal
        if (handle.includes('w')) {
          const maxDx = state.startW - MIN_SIZE_PERCENT;
          const clampedDx = clamp(dxPercent, -state.startX, maxDx);
          newX = state.startX + clampedDx;
          newW = state.startW - clampedDx;
        } else if (handle.includes('e')) {
          newW = clamp(state.startW + dxPercent, MIN_SIZE_PERCENT, 100 - state.startX);
        }

        // Vertical
        if (handle.includes('n')) {
          const maxDy = state.startH - MIN_SIZE_PERCENT;
          const clampedDy = clamp(dyPercent, -state.startY, maxDy);
          newY = state.startY + clampedDy;
          newH = state.startH - clampedDy;
        } else if (handle.includes('s')) {
          newH = clamp(state.startH + dyPercent, MIN_SIZE_PERCENT, 100 - state.startY);
        }

        newPos = {
          xPercent: round4(newX),
          yPercent: round4(newY),
          widthPercent: round4(newW),
          heightPercent: round4(newH),
        };
      }

      lastPositionRef.current = newPos;
      onPositionChange(state.fieldId, newPos);
    };

    const handlePointerUp = () => {
      const state = interactionRef.current;
      if (!state) return;

      if (lastPositionRef.current) {
        onPositionCommit(state.fieldId, lastPositionRef.current);
      }

      setInteraction(null);
      interactionRef.current = null;
      lastPositionRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [containerRef, onPositionChange, onPositionCommit]);

  return {
    interactionState: interaction,
    startDrag,
    startResize,
  };
}
